import { ForgeRockStep } from "@services/forgeRockAuthService"
import {
	StoredStep,
	ForgeRockCallback,
	ForgeRockCallbackOutput
} from "@models/forgeRock.model"
import { StepNavigationMethods } from "@models/forgeRock.model"
import { VITE_ENABLE_FORGEROCK_LOGS, ERROR_CODES } from "./constants.util"

// ===== CONSOLE LOGGING UTILITIES =====
// This function is disabled in production to prevent exposing sensitive journey details

export const createLogStepInfo = (journeyId: string) => {
	return (
		action: string,
		stepNumber: number,
		step?: ForgeRockStep,
		additionalData?: Record<string, unknown>
	) => {
		// Only log if enabled via environment variable
		if (!VITE_ENABLE_FORGEROCK_LOGS) {
			return
		}

		// Log only non-sensitive metadata - never actual values or journey IDs
		const logData = {
			journeyId,
			action,
			stepNumber,
			stepType: step?.type || "unknown",
			timestamp: new Date().toISOString(),
			callbacksCount: step?.callbacks?.length || 0,
			...additionalData
		}
		console.group(`🔐 ForgeRock Journey - ${action}`)
		console.log("Step Info:", logData)
		if (step?.callbacks) {
			console.log(
				"Callbacks:",
				step.callbacks.map((cb: ForgeRockCallback, idx: number) => {
					const callbackType = cb.getType?.() || cb.type

					// Only try to get input value for callbacks that have input (not TextOutputCallback)
					let value = undefined
					if (callbackType !== "TextOutputCallback") {
						try {
							value = cb.getInputValue?.()
						} catch (_e) {
							// Ignore errors for callbacks without input values
							value = undefined
						}
					}

					return {
						index: idx,
						type: callbackType,
						prompt: cb.getPrompt?.(),
						value,
						// For TextOutputCallback, show the output message instead
						message:
							callbackType === "TextOutputCallback"
								? extractCallbackMessage(cb)
								: undefined
					}
				})
			)
		}
		console.groupEnd()
	}
}

export const createLogNavigationAttempt = (
	journeyId: string,
	stepsHistory: Map<number, StoredStep>
) => {
	return (
		action: string,
		fromStep: number,
		toStep: number,
		success: boolean,
		reason?: string
	) => {
		// Only log if enabled via environment variable
		if (!VITE_ENABLE_FORGEROCK_LOGS) {
			return
		}

		console.log(`🧭 Navigation ${success ? "✅" : "❌"}:`, {
			journeyId,
			action,
			fromStep,
			toStep,
			success,
			reason: reason || undefined,
			availableSteps: Array.from(stepsHistory.keys()),
			timestamp: new Date().toISOString()
		})
	}
}

export const createLogStepsHistory = (
	stepsHistory: Map<number, StoredStep>
) => {
	return () => {
		// Only log if enabled via environment variable
		if (!VITE_ENABLE_FORGEROCK_LOGS) {
			return
		}

		const history = Array.from(stepsHistory.values()).sort(
			(a, b) => a.stepNumber - b.stepNumber
		)
		console.group("📚 Steps History")
		console.table(
			history.map((step) => ({
				stepNumber: step.stepNumber,
				stepType: step.stepType,
				timestamp: new Date(step.timestamp).toLocaleTimeString(),
				callbackValuesCount: Object.keys(step.callbackValues).length
			}))
		)
		console.groupEnd()
	}
}

// ===== CALLBACK PROCESSING UTILITIES =====
export const extractCallbackMessage = (
	callback: ForgeRockCallback
): string | null => {
	// Extract message from raw callback output
	if (callback.output) {
		const messageOutput = callback.output.find(
			(output: ForgeRockCallbackOutput) => output.name === "message"
		)
		return messageOutput?.value?.toString() || null
	}

	// Extract message from SDK callback methods
	if (callback.getOutputValue) {
		return callback.getOutputValue() || null
	}

	// Extract message from getMessage method if available
	if (callback.getMessage) {
		return callback.getMessage() || null
	}

	return null
}

export const isErrorMessage = (
	callback: ForgeRockCallback,
	message: string
): boolean => {
	// Check if it's an error message (messageType "2" typically indicates error)
	const hasErrorType = callback.output?.some(
		(output: ForgeRockCallbackOutput) =>
			output.name === "messageType" &&
			(output.value === "2" || output.value === 2)
	)

	const hasErrorKeywords =
		message.toLowerCase().includes("invalid") ||
		message.toLowerCase().includes("expired") ||
		message.toLowerCase().includes("error") ||
		message.toLowerCase().includes("failed")

	return hasErrorType || hasErrorKeywords
}

// ===== ERROR MESSAGE MAPPER =====
export interface ErrorTypeMapping {
	pattern: RegExp
	type: string
	messageTemplate: (remainingAttempts: number) => string
}

export const ERROR_TYPE_MAPPINGS: ErrorTypeMapping[] = [
	{
		pattern: /EXPIRED_OTP:.*Expired\s*OTP/i,
		type: "EXPIRED_OTP",
		messageTemplate: () => "Code Expired"
	},
	{
		pattern: /INVALID_OTP:.*Invalid\s*OTP/i,
		type: "INVALID_OTP",
		messageTemplate: () =>
			"Sorry that is not the correct authentication code. Please check the code and try again. Or click below to receive a new code."
	},
	{
		pattern: /Invalid\s*Credentials:\s*InvalidCredentials$/i,
		type: "INVALID_USERNAME",
		messageTemplate: () => "Your attempt to sign-in was unsuccessful."
	},
	{
		pattern: /Invalid\s*Credentials.*remainder\s+count:(\d+)/i,
		type: "INVALID_CREDENTIALS",
		messageTemplate: () => "Your attempt to sign in was unsuccessful."
	},
	{
		pattern: /invalidUserPassword.*remainder\s+count:(\d+)/i,
		type: "INVALID_CREDENTIALS",
		messageTemplate: () => "Your attempt to sign in was unsuccessful."
	},
	{
		pattern: /Locked\s*Users:\s*UserLockedAccount/i,
		type: "ACCOUNT_LOCKED",
		messageTemplate: () => "Account Locked"
	},
	{
		pattern: /Account\s*Locked/i,
		type: "ACCOUNT_LOCKED",
		messageTemplate: () => "Account Locked"
	},
	{
		pattern: /Invalid\s*Username/i,
		type: "INVALID_USERNAME",
		messageTemplate: () => "The username you entered is invalid."
	}
]

/**
 * Parse ForgeRock error messages and extract structured error information
 * Supports multiple error patterns including:
 * - "Invalid Credentials: InvalidCredentials remainder count:4"
 * - "invalidUserPassword remainder count:4"
 */
export const parseForgeRockErrorMessage = (
	message: string
): {
	errorType: string | null
	remainingAttempts: number | null
	userFriendlyMessage: string | null
} => {
	for (const mapping of ERROR_TYPE_MAPPINGS) {
		const match = message.match(mapping.pattern)
		if (match) {
			const remainingAttempts = match[1] ? Number.parseInt(match[1], 10) : null
			return {
				errorType: mapping.type,
				remainingAttempts,
				userFriendlyMessage:
					remainingAttempts !== null
						? mapping.messageTemplate(remainingAttempts)
						: mapping.messageTemplate(0)
			}
		}
	}

	return {
		errorType: null,
		remainingAttempts: null,
		userFriendlyMessage: null
	}
}

/**
 * @deprecated Use parseForgeRockErrorMessage instead
 * Parse ForgeRock invalidUserPassword message to extract attempt count
 * Example: "invalidUserPassword remainder count:4" -> { isInvalidPassword: true, remainingAttempts: 4 }
 */
export const parseInvalidUserPasswordMessage = (
	message: string
): { isInvalidPassword: boolean; remainingAttempts: number | null } => {
	const result = parseForgeRockErrorMessage(message)

	return {
		isInvalidPassword: result.errorType === "INVALID_CREDENTIALS",
		remainingAttempts: result.remainingAttempts
	}
}

export const parseForgeRock401Error = (payload: {
	code?: string | number
	message?: string
	reason?: string
}): {
	errorCode: string
	remainingAttempts: number
	errorMessage: string
} => {
	const code = payload.code?.toString()
	const message = payload.message || payload.reason || "Authentication failed"

	if (code !== "401") {
		return {
			errorCode: code || "FORGEROCK_ERROR",
			remainingAttempts: 0,
			errorMessage: message
		}
	}

	if (
		message === "UserLockedAccount" ||
		message.toLowerCase().includes("locked")
	) {
		return {
			errorCode: ERROR_CODES.ACCOUNT_LOCKED,
			remainingAttempts: 0,
			errorMessage: message
		}
	}

	if (message.includes("attemptsRemaining")) {
		const countMatch = message.match(/count[:\s]*(\d+)/i)
		const extractedCount = countMatch
			? Number.parseInt(countMatch[1], 10)
			: null
		return {
			errorCode: ERROR_CODES.INVALID_CREDENTIALS,
			remainingAttempts: extractedCount ?? 0,
			errorMessage: message
		}
	}

	if (message === "unsuccessfulLoginMessage") {
		return {
			errorCode: ERROR_CODES.INVALID_USERNAME,
			remainingAttempts: 0,
			errorMessage: message
		}
	}

	return {
		errorCode: "401",
		remainingAttempts: 0,
		errorMessage: message
	}
}

/**
 * Detects whether a ForgeRock error or step payload indicates a session timeout.
 * Used to transparently restart the authentication journey when the user idles
 * on the login page and the server-side session expires.
 */
export const isSessionTimeoutError = (errorOrPayload: unknown): boolean => {
	if (!errorOrPayload || typeof errorOrPayload !== "object") return false

	const obj = errorOrPayload as Record<string, unknown>
	const detail = obj.detail as Record<string, string>

	if (obj.code === 401 && detail?.errorCode === "110") {
		return true
	}

	return false
}

// ===== JOURNEY UTILITIES =====
export const generateJourneyId = (): string => {
	return `journey-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export const exposeNavigationMethodsToWindow = (
	navigationMethods: StepNavigationMethods
) => {
	// Expose navigation methods globally for debugging (only in development)
	if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
		;(
			window as unknown as { forgeRockNavigation: StepNavigationMethods }
		).forgeRockNavigation = navigationMethods
	}
}

// ===== MFA DETECTION UTILITIES =====
export interface MFATextOutputConfig {
	otpExpiry: number // minutes until OTP expires
	adminEmail: string // admin email (or "undefined")
	primaryMfa: string // primary method (e.g. "email")
	secondaryMfa: string // secondary method (e.g. "none", "sms")
	deviceDays: number // days for "remember device"
	timelapse: number // minutes to wait between resend requests
	otpTimestamp: number // OTP generation timestamp
}

/**
 * Parses the TextOutputCallback message into a structured config object.
 * Expected format: "otpexpiry:5 | adminemail:undefined | primarymfa:email | secondaymfa:none | device:30 | timelapse:2 | OTP Timestamp (ms):  1770828959"
 */
export const parseMFATextOutput = (
	message: string
): MFATextOutputConfig | null => {
	if (!message) return null

	const parts = message.split("|").map((s) => s.trim())
	const config: Record<string, string> = {}

	for (const part of parts) {
		const colonIdx = part.indexOf(":")
		if (colonIdx === -1) continue
		const key = part.substring(0, colonIdx).trim().toLowerCase()
		const value = part.substring(colonIdx + 1).trim()
		config[key] = value
	}

	// Bail out if we didn't find any recognizable keys
	if (!config.otpexpiry && !config.timelapse && !config.device) {
		return null
	}

	return {
		otpExpiry: Number(config.otpexpiry) || 5,
		adminEmail: config.adminemail || "",
		primaryMfa: config.primarymfa || "",
		secondaryMfa: config.secondaymfa || "none",
		deviceDays: Number(config.device) || 30,
		timelapse: Number(config.timelapse) || 1,
		otpTimestamp: Number(config["otp timestamp (ms)"]) || 0
	}
}

export interface MFACallbacks {
	otpCallback: ForgeRockCallback | null
	choiceCallback: ForgeRockCallback | null
	rememberDeviceCallback: ForgeRockCallback | null
	textOutputConfig: MFATextOutputConfig | null
	otpCallbackIndex: number
	choiceCallbackIndex: number
	rememberDeviceCallbackIndex: number
}

/**
 * Detects if a ForgeRock step contains MFA callbacks
 * Returns structured information about MFA callbacks if found
 */
export const detectMFACallbacks = (
	step: ForgeRockStep | null
): MFACallbacks | null => {
	if (!step?.callbacks || step.callbacks.length === 0) {
		return null
	}

	let otpCallback: ForgeRockCallback | null = null
	let choiceCallback: ForgeRockCallback | null = null
	let rememberDeviceCallback: ForgeRockCallback | null = null
	let textOutputConfig: MFATextOutputConfig | null = null
	let otpCallbackIndex = -1
	let choiceCallbackIndex = -1
	let rememberDeviceCallbackIndex = -1

	step.callbacks.forEach((callback: ForgeRockCallback, index: number) => {
		const callbackType = callback.getType?.() || callback.type

		// Extract MFA config from TextOutputCallback (non-error messages)
		if (callbackType === "TextOutputCallback") {
			const message = extractCallbackMessage(callback)
			if (message && !isErrorMessage(callback, message)) {
				textOutputConfig = parseMFATextOutput(message)
			}
		}

		// Check for PasswordCallback with "OTP Code" prompt
		if (callbackType === "PasswordCallback") {
			const prompt =
				callback.getPrompt?.() ||
				callback.output
					?.find((out: ForgeRockCallbackOutput) => out.name === "prompt")
					?.value?.toString()

			if (prompt === "OTP Code" || prompt?.toLowerCase().includes("otp")) {
				otpCallback = callback
				otpCallbackIndex = index
			}
		}

		// Check for ChoiceCallback with OTP-related choices
		if (callbackType === "ChoiceCallback") {
			const choices = callback.output?.find(
				(out: ForgeRockCallbackOutput) => out.name === "choices"
			)?.value

			if (
				Array.isArray(choices) &&
				choices.some((choice: string) => choice.toLowerCase().includes("otp"))
			) {
				choiceCallback = callback
				choiceCallbackIndex = index
			}
		}

		// Check for BooleanAttributeInputCallback with "Remember me" or similar
		if (callbackType === "BooleanAttributeInputCallback") {
			const name =
				callback.output
					?.find((out: ForgeRockCallbackOutput) => out.name === "name")
					?.value?.toString() ||
				callback.output
					?.find((out: ForgeRockCallbackOutput) => out.name === "prompt")
					?.value?.toString()

			if (
				name?.toLowerCase().includes("remember") ||
				name?.toLowerCase().includes("device")
			) {
				rememberDeviceCallback = callback
				rememberDeviceCallbackIndex = index
			}
		}
	})

	// MFA is detected if we have at least an OTP callback
	if (otpCallback) {
		return {
			otpCallback,
			choiceCallback,
			rememberDeviceCallback,
			otpCallbackIndex,
			choiceCallbackIndex,
			rememberDeviceCallbackIndex,
			textOutputConfig
		}
	}

	return null
}

// ===== DEVICE PROFILING UTILITIES =====
/**
 * Detects if a ForgeRock step contains a DeviceProfileCallback
 * Returns the callback and its index if found
 */
export const detectDeviceProfileCallback = (
	step: ForgeRockStep | null
): { callback: ForgeRockCallback; index: number } | null => {
	if (!step?.callbacks || step.callbacks.length === 0) {
		return null
	}

	for (let index = 0; index < step.callbacks.length; index++) {
		const callback = step.callbacks[index]
		const callbackType = callback.getType?.() || callback.type

		if (callbackType === "DeviceProfileCallback") {
			return { callback, index }
		}
	}

	return null
}

/**
 * Checks if a step contains only DeviceProfileCallback (no other input callbacks)
 * This indicates the step should be auto-submitted after device profiling
 */
export const isDeviceProfileOnlyStep = (
	step: ForgeRockStep | null
): boolean => {
	if (!step?.callbacks || step.callbacks.length === 0) {
		return false
	}

	const deviceProfileCallback = detectDeviceProfileCallback(step)
	if (!deviceProfileCallback) {
		return false
	}

	// Check if there are any other input callbacks besides DeviceProfileCallback
	const hasOtherInputCallbacks = step.callbacks.some(
		(callback: ForgeRockCallback, index: number) => {
			if (index === deviceProfileCallback.index) {
				return false // Skip the DeviceProfileCallback itself
			}
			const callbackType = callback.getType?.() || callback.type
			// Only NameCallback and PasswordCallback are considered input callbacks
			return (
				callbackType === "NameCallback" || callbackType === "PasswordCallback"
			)
		}
	)

	return !hasOtherInputCallbacks
}
